// Mes Commandes - Page pour les ACHETEURS
// Connectée aux vraies APIs

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Package, Truck, CheckCircle2, Clock, XCircle, ChevronRight,
  ArrowLeft, Search, Filter, Eye, RotateCcw, MessageSquare,
  Home, ShoppingBag, Heart, User, MapPin, Calendar, AlertCircle,
  CreditCard, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { ordersAPI, cartAPI, authAPI } from '../../services/buyerAPI';

// Statuts des commandes avec mapping API
const ORDER_STATUSES = {
  pending: { color: 'bg-yellow-100 text-yellow-700', label: 'En attente', icon: Clock },
  paid: { color: 'bg-blue-100 text-blue-700', label: 'Payée', icon: CreditCard },
  confirmed: { color: 'bg-blue-100 text-blue-700', label: 'Confirmée', icon: CheckCircle2 },
  preparing: { color: 'bg-indigo-100 text-indigo-700', label: 'En préparation', icon: Package },
  shipped: { color: 'bg-purple-100 text-purple-700', label: 'Expédiée', icon: Truck },
  delivered: { color: 'bg-green-100 text-green-700', label: 'Livrée', icon: CheckCircle2 },
  cancelled: { color: 'bg-red-100 text-red-700', label: 'Annulée', icon: XCircle },
  refunded: { color: 'bg-gray-100 text-gray-700', label: 'Remboursée', icon: RefreshCw },
};

// Carte commande acheteur
function OrderCard({ order, onViewDetails, onCancel, onBuyAgain }) {
  const status = ORDER_STATUSES[order.status] || ORDER_STATUSES.pending;
  const StatusIcon = status.icon;

  // Extraire tous les items de tous les vendeurs
  const allItems = order.sellers?.flatMap(s => 
    s.items.map(item => ({ ...item, seller_name: s.seller_name }))
  ) || [];

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const canCancel = ['pending', 'paid', 'confirmed'].includes(order.status);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all"
    >
      {/* Header avec date et statut */}
      <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center gap-4 text-sm flex-wrap">
          <span className="text-gray-500">
            Commande du <span className="font-medium text-gray-900">{formatDate(order.created_at)}</span>
          </span>
          <span className="text-gray-300 hidden sm:inline">|</span>
          <span className="font-mono text-gray-500 text-xs sm:text-sm">{order.order_number}</span>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
          <StatusIcon size={12} />
          {status.label}
        </span>
      </div>

      {/* Produits */}
      <div className="p-4">
        <div className="space-y-3">
          {allItems.slice(0, 3).map((item, idx) => (
            <div key={idx} className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-lg bg-gray-100 flex-shrink-0 flex items-center justify-center overflow-hidden">
                {item.product_image ? (
                  <img src={item.product_image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Package size={24} className="text-gray-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 line-clamp-1">{item.product_name}</h3>
                <p className="text-sm text-gray-500">Quantité: {item.quantity}</p>
                <p className="text-sm font-semibold text-gray-900">{item.total_price?.toFixed(2) || item.unit_price?.toFixed(2)} €</p>
              </div>
            </div>
          ))}
          {allItems.length > 3 && (
            <p className="text-sm text-gray-500 text-center py-2">
              + {allItems.length - 3} autre(s) article(s)
            </p>
          )}
        </div>

        {/* Vendeur(s) */}
        {order.sellers && order.sellers.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap">
            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
              <ShoppingBag size={14} className="text-orange-600" />
            </div>
            <span className="text-sm text-gray-600">
              Vendu par{' '}
              <span className="font-medium text-gray-900">
                {order.sellers.map(s => s.seller_name).join(', ')}
              </span>
            </span>
          </div>
        )}

        {/* Suivi livraison */}
        {order.status === 'shipped' && order.sellers?.some(s => s.tracking_number) && (
          <div className="mt-3 p-3 bg-purple-50 rounded-lg">
            <p className="text-sm text-purple-700 flex items-center gap-2">
              <Truck size={16} />
              N° de suivi : <span className="font-mono">{order.sellers.find(s => s.tracking_number)?.tracking_number}</span>
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to={`/commande/${order.id}`}>
            <Button
              variant="outline"
              size="sm"
              className="rounded-lg"
            >
              <Eye size={14} className="mr-1" /> Suivre ma commande
            </Button>
          </Link>
          {order.status === 'delivered' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onBuyAgain(order)}
              className="rounded-lg"
            >
              <RotateCcw size={14} className="mr-1" /> Acheter à nouveau
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCancel(order)}
              className="rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <XCircle size={14} className="mr-1" /> Annuler
            </Button>
          )}
        </div>
      </div>

      {/* Footer avec total */}
      <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-100">
        <span className="text-sm text-gray-500">Total de la commande</span>
        <span className="text-lg font-bold text-gray-900">{order.total?.toFixed(2)} €</span>
      </div>
    </motion.div>
  );
}

export default function MesCommandesPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      toast.error('Connecte-toi pour voir tes commandes');
      navigate('/connexion');
      return;
    }
    fetchOrders();
  }, [navigate]);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ordersAPI.getAll(null, 50);
      setOrders(data);
    } catch (e) {
      console.error('Error fetching orders:', e);
      setError(e.message);
      if (e.message.includes('401') || e.message.includes('authentifié')) {
        toast.error('Session expirée, reconnecte-toi');
        navigate('/connexion');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (order) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette commande ?')) return;
    
    try {
      await ordersAPI.cancel(order.id);
      toast.success('Commande annulée');
      fetchOrders();
    } catch (e) {
      toast.error(e.message || 'Erreur lors de l\'annulation');
    }
  };

  const handleBuyAgain = async (order) => {
    const allItems = order.sellers?.flatMap(s => s.items) || [];
    let added = 0;
    
    for (const item of allItems) {
      try {
        await cartAPI.addItem({
          id: item.product_id,
          name: item.product_name,
          price: item.unit_price,
          image: item.product_image,
          seller_id: order.sellers?.find(s => s.items.includes(item))?.seller_id,
          seller_name: order.sellers?.find(s => s.items.includes(item))?.seller_name
        }, item.quantity);
        added++;
      } catch (e) {
        console.error('Error adding to cart:', e);
      }
    }
    
    if (added > 0) {
      toast.success(`${added} article(s) ajouté(s) au panier`);
    }
  };

  const filteredOrders = orders.filter(o => {
    const matchesFilter = filter === 'all' || o.status === filter;
    const matchesSearch = searchQuery === '' || 
      (o.order_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.sellers?.some(s => 
        s.items.some(i => (i.product_name || '').toLowerCase().includes(searchQuery.toLowerCase()))
      );
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mes-commandes-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Mes commandes</h1>
              <p className="text-sm text-gray-500">{orders.length} commande(s)</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="text-red-500" size={20} />
            <span className="text-red-700">{error}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchOrders}
              className="ml-auto text-red-600 hover:text-red-700"
            >
              Réessayer
            </Button>
          </div>
        )}

        {/* Filtres */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Rechercher une commande..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-xl"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm"
            >
              <option value="all">Toutes les commandes</option>
              <option value="pending">En attente</option>
              <option value="paid">Payées</option>
              <option value="preparing">En préparation</option>
              <option value="shipped">En cours de livraison</option>
              <option value="delivered">Livrées</option>
              <option value="cancelled">Annulées</option>
            </select>
          </div>
        </div>

        {/* Liste des commandes */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <Package size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="font-bold text-gray-900 mb-2">
              {orders.length === 0 ? 'Aucune commande' : 'Aucun résultat'}
            </h3>
            <p className="text-gray-500 mb-6">
              {orders.length === 0 
                ? 'Vous n\'avez pas encore passé de commande' 
                : 'Aucune commande ne correspond à ta recherche'}
            </p>
            {orders.length === 0 && (
              <Button 
                onClick={() => navigate('/produits')}
                className="bg-orange-500 hover:bg-orange-600 rounded-xl"
              >
                Découvrir nos produits
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onViewDetails={(o) => navigate(`/mes-commandes/${o.id}`)}
                onCancel={handleCancel}
                onBuyAgain={handleBuyAgain}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
